
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {collectLoadedGeometryNodes} from "../LER/LERParser.js";

// Colors a point cloud by its own physical deviation from a reference
// plane the user defines by eye (3 clicks - see index.html's "Definér
// referenceplan") - the same idea as LE34's own as-built elevation
// reports (a wall's scanned surface, colored by how far it strays from an
// intended flat/plumb design plane), just interactive instead of a
// generated PDF sheet: pick a plane, see the colored deviation live in the
// viewer, adjust and re-pick if the plane wasn't quite right.

// Diverging colormap matching that report's own legend: dark blue (well
// inside the design plane, -50mm) through cyan, green ("Design", 0mm),
// yellow, orange, red, up to magenta/purple (well outside, +50mm).
// Piecewise-linear through hand-picked stops (not a generic HSV sweep) so
// 0mm reads as one clean, unambiguous green the way the reference does.
const STOPS = [
	[-50, [30, 30, 130]],
	[-25, [40, 110, 220]],
	[-10, [80, 200, 230]],
	[0, [60, 200, 80]],
	[10, [235, 220, 60]],
	[25, [235, 140, 40]],
	[40, [220, 50, 50]],
	[50, [150, 30, 150]],
];

// rangeMm scales the fixed -50..+50 STOPS above onto whatever ± interval
// the user has actually dialed in ("Vis afvigelse ± (mm)") - without this,
// a point cloud checked against a tight 5mm tolerance would still only ever
// reach the pale near-green end of the colormap (5 is a sliver of the fixed
// ±50 the STOPS were authored for), never the red/purple extremes the
// legend promises. Scaling the input mm by 50/rangeMm before looking it up
// in STOPS reuses the exact same hand-picked colors/thresholds, just
// stretched or compressed onto the caller's own range.
export function colorForDeviationMm(mm, rangeMm = 50){
	let scaledMm = rangeMm > 0 ? mm * (50 / rangeMm) : mm;
	let clamped = Math.max(STOPS[0][0], Math.min(STOPS[STOPS.length - 1][0], scaledMm));

	for(let i = 0; i < STOPS.length - 1; i++){
		let [a, colorA] = STOPS[i];
		let [b, colorB] = STOPS[i + 1];

		if(clamped >= a && clamped <= b){
			let t = (clamped - a) / (b - a);
			return [
				Math.round(colorA[0] + (colorB[0] - colorA[0]) * t),
				Math.round(colorA[1] + (colorB[1] - colorA[1]) * t),
				Math.round(colorA[2] + (colorB[2] - colorA[2]) * t),
			];
		}
	}

	return STOPS[STOPS.length - 1][1];
}

// The exact stops as CSS rgb() color-stop entries, for building a legend
// gradient in HTML that's guaranteed to match what applyDeviationColoring
// actually paints - one source of truth (the STOPS above) rather than a
// hand-authored CSS gradient that could quietly drift out of sync.
export function deviationLegendCssStops(){
	let [minMm] = STOPS[0];
	let [maxMm] = STOPS[STOPS.length - 1];
	let range = maxMm - minMm;

	return STOPS.map(([mm, [r, g, b]]) => {
		let percent = ((mm - minMm) / range) * 100;
		return `rgb(${r},${g},${b}) ${percent}%`;
	}).join(", ");
}

// geometryNode -> Uint8Array snapshot of its original "rgba" attribute,
// so restoreOriginalColoring can put the true scan colors back exactly
// rather than just re-deriving *a* plausible color.
const originalColorsByNode = new WeakMap();

// Scanningens egne farver for en node, som applyDeviationColoring har farvet om (ellers null). Eksporter (Snit, 2D-profil) bruger dem, så
// afvigelsesfarverne ikke havner i en almindelig punktskyeksport - kun afvigelseseksporten skal have dem.
export function originalRgbaForNode(geometryNode){
	return originalColorsByNode.get(geometryNode) || null;
}

// Recolors every currently-loaded point (see collectLoadedGeometryNodes's
// own comment: the *whole* loaded octree, not just this frame's
// camera-visible subset, so a small pan/zoom right afterwards doesn't
// reveal still-original-colored points at the edges) within maxDistanceM
// of the given plane. Points further away are left with their original
// color entirely, rather than pinned to the extreme color, so an
// unclipped view of the whole building doesn't wash out into solid
// blue/purple away from the wall being checked.
export function applyDeviationColoring(pointcloud, planePoint, planeNormal, maxDistanceM = 0.5){
	let normal = planeNormal.clone().normalize();
	let world = new THREE.Vector3();
	let diff = new THREE.Vector3();
	// Same field drives both "how far from the plane counts at all" and
	// "what the full colormap spans" - see colorForDeviationMm's own
	// comment on why that scaling is needed.
	let rangeMm = maxDistanceM * 1000;

	for(let geometryNode of collectLoadedGeometryNodes(pointcloud.root)){
		let geometry = geometryNode.geometry;
		let rgba = geometry.attributes.rgba;
		if(!rgba){
			continue;
		}

		if(!originalColorsByNode.has(geometryNode)){
			originalColorsByNode.set(geometryNode, rgba.array.slice());
		}

		let numPoints = geometryNode.numPoints;
		let position = geometry.attributes.position.array;

		let nodeMatrix = new THREE.Matrix4().makeTranslation(...geometryNode.boundingBox.min.toArray());
		let matrix = new THREE.Matrix4().multiplyMatrices(pointcloud.matrixWorld, nodeMatrix);

		for(let i = 0; i < numPoints; i++){
			world.set(position[i * 3], position[i * 3 + 1], position[i * 3 + 2]);
			world.applyMatrix4(matrix);

			let distanceM = diff.subVectors(world, planePoint).dot(normal);
			if(Math.abs(distanceM) > maxDistanceM){
				continue;
			}

			let [r, g, b] = colorForDeviationMm(distanceM * 1000, rangeMm);
			rgba.array[i * 4 + 0] = r;
			rgba.array[i * 4 + 1] = g;
			rgba.array[i * 4 + 2] = b;
		}

		rgba.needsUpdate = true;
	}
}

// Puts back the original scan RGB wherever applyDeviationColoring changed
// it - walks the same loaded-node set, so it correctly restores nodes that
// have since been evicted from view and reloaded too (a fresh load has no
// entry here and is simply skipped, already showing its true colors).
export function restoreOriginalColoring(pointcloud){
	for(let geometryNode of collectLoadedGeometryNodes(pointcloud.root)){
		let original = originalColorsByNode.get(geometryNode);
		if(!original){
			continue;
		}

		let rgba = geometryNode.geometry.attributes.rgba;
		rgba.array.set(original);
		rgba.needsUpdate = true;
		originalColorsByNode.delete(geometryNode);
	}
}

// The exact same points applyDeviationColoring above tints (within
// maxDistanceM of the plane) - collected as flat world-space xyz triples
// plus their current (already-colored) RGB, normalized to 0-1 as
// LASExporter.toLAS expects. Deliberately re-walks and re-computes the same
// distance test rather than reading back whichever points happen to still
// carry a non-original color, so export always matches "what's shown
// colored right now" even if applyDeviationColoring was called more than
// once (e.g. after a margin change) since the two are meant to be called
// with the same arguments in lockstep - see index.html's own
// lastAppliedDeviation, which tracks exactly that pairing.
export function collectDeviationColoredPoints(pointcloud, planePoint, planeNormal, maxDistanceM){
	let normal = planeNormal.clone().normalize();
	let world = new THREE.Vector3();
	let diff = new THREE.Vector3();
	let positions = [];
	let colors = [];

	for(let geometryNode of collectLoadedGeometryNodes(pointcloud.root)){
		let geometry = geometryNode.geometry;
		let rgba = geometry.attributes.rgba;
		if(!rgba){
			continue;
		}

		let numPoints = geometryNode.numPoints;
		let position = geometry.attributes.position.array;
		let nodeMatrix = new THREE.Matrix4().makeTranslation(...geometryNode.boundingBox.min.toArray());
		let matrix = new THREE.Matrix4().multiplyMatrices(pointcloud.matrixWorld, nodeMatrix);

		for(let i = 0; i < numPoints; i++){
			world.set(position[i * 3], position[i * 3 + 1], position[i * 3 + 2]);
			world.applyMatrix4(matrix);

			let distanceM = diff.subVectors(world, planePoint).dot(normal);
			if(Math.abs(distanceM) > maxDistanceM){
				continue;
			}

			positions.push(world.x, world.y, world.z);
			colors.push(
				rgba.array[i * 4 + 0] / 255,
				rgba.array[i * 4 + 1] / 255,
				rgba.array[i * 4 + 2] / 255,
				1);
		}
	}

	return {positions, colors};
}
